import type { Interpolation } from '@emotion/react';
import { css, keyframes } from '@emotion/react';

import { breakpoints } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import {
  DEFAULT_LOGO_SIZE,
  PLACEHOLDER_LINE_HEIGHT,
  pixelMarginBetweenAds,
} from 'bundles/promo-units/components/banner-ad/constants';
import { numToPx } from 'bundles/promo-units/components/banner-ad/utils';

const shimmerAnimationDuration = 1.3;
const shimmerKeyframes = keyframes`
  0% {
    background-position: -468px 0;
  }
  100% {
    background-position: 468px 0;
  }
`;

export const styles: Record<string, Interpolation<Theme>> = {
  bannerAdOuterContainer: css`
    box-sizing: border-box;
    flex: 1 1 auto;
    display: flex;
    height: 100%;

    & > div:first-of-type {
      /* for TrackedDiv ref  */
      flex-grow: 1;
    }
  `,
  /* Fall back to white bg with gray border. This should always be overridden by one of the color classes below. */
  bannerAdInnerContainer: () => css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    overflow: hidden;
    border: 1px solid var(--cds-color-grey-50);
    border-radius: 4px;
    cursor: pointer;
    background-color: #fff;
    border-color: #efefef;
    min-width: 448px;
    width: 100%;
    height: 100%;

    /* Mobile min width - cuts off text at 320px */
    ${breakpoints.down('xs')} {
      min-width: 280px;
    }

    /* TODO align with CDS colors (from theme) */

    &.dodgerBlue {
      background-color: #1e94eb;
      border-color: #1e94eb;
    }

    &.froly {
      background-color: #f26c75;
      border-color: #f26c75;
    }

    &.white {
      background-color: #fff;
      border-color: #efefef;
    }

    &.goldenPoppy {
      background-color: #f3c800;
      border-color: #f3c800;
    }

    &.lightCyan {
      background-color: #d7eef7;
      border-color: #d7eef7;
    }
  `,
  bannerAdInnerContainerResponsiveImage: () => css`
    ${breakpoints.only('sm')} {
      flex-direction: column-reverse;
      width: fit-content;
      max-width: 100vw;
      margin: auto;
      height: initial;
    }
  `,
  partner: () => css`
    min-height: 1em;
    line-height: 1.25rem;
  `,
  partnerPlaceholder: () => css`
    max-width: 150px;
    min-height: ${PLACEHOLDER_LINE_HEIGHT}px;
    height: ${PLACEHOLDER_LINE_HEIGHT}px;
    width: 100px;
    background-color: var(--cds-color-grey-50);

    ${breakpoints.down('xs')} {
      margin: auto;
    }

    margin-bottom: var(--cds-spacing-100);
    animation-delay: ${shimmerAnimationDuration / 4}s;
  `,
  title: () => css`
    margin-top: var(--cds-spacing-100);
    margin-bottom: var(--cds-spacing-150);

    a {
      color: inherit;

      &:hover {
        color: inherit;
      }
    }
  `,
  titlePlaceholder: () => css`
    max-width: 200px;
    min-height: ${PLACEHOLDER_LINE_HEIGHT}px;
    height: ${PLACEHOLDER_LINE_HEIGHT}px;
    width: 200px;
    background-color: var(--cds-color-grey-50);
    animation-delay: ${shimmerAnimationDuration / 4}s;
  `,
  bodyText: () => css`
    margin-bottom: var(--cds-spacing-300);
  `,
  bodyTextPlaceholder: () => css`
    max-width: 250px;
    min-height: ${PLACEHOLDER_LINE_HEIGHT}px;
    height: ${PLACEHOLDER_LINE_HEIGHT}px;
    width: 250px;
    background-color: var(--cds-color-grey-50);
    animation-delay: ${shimmerAnimationDuration / 4}s;
  `,
  leftSideContainer: (theme) => css`
    display: flex;
    flex-direction: column;
    align-items: start;
    justify-content: space-between;
    margin: ${theme.spacing(pixelMarginBetweenAds)};
    max-height: 100%;
    flex-grow: 1;
    max-width: 28em;
    min-width: 14rem;

    ${breakpoints.down('sm')} {
      max-width: none;
    }

    ${breakpoints.down('xs')} {
      text-align: center;
      align-items: center;
      max-width: initial;
      margin-left: var(--cds-spacing-150);
      margin-right: var(--cds-spacing-150);
    }
  `,
  ctaWrapper: () => css`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;

    .logo {
      width: ${numToPx(DEFAULT_LOGO_SIZE.width)};
      height: ${numToPx(DEFAULT_LOGO_SIZE.height)};

      ${breakpoints.down('xs')} {
        display: inline-block;
      }
    }
  `,
  ctaPlaceholder: () => css`
    max-width: 10em;
    min-height: ${PLACEHOLDER_LINE_HEIGHT}px;
    height: ${PLACEHOLDER_LINE_HEIGHT}px;
    width: 130px;
    background-color: var(--cds-color-grey-50);
    animation-delay: ${shimmerAnimationDuration / 4}s;
  `,
  rightSideContainer: () => css`
    img {
      position: relative;
      height: 100%;
      object-fit: contain;
    }

    ${breakpoints.down('xs')} {
      display: none;
    }
  `,
  shimmer: () => css`
    animation-name: ${shimmerKeyframes};
    animation-duration: ${shimmerAnimationDuration}s;
    animation-iteration-count: infinite;
    animation-fill-mode: forwards;
    animation-timing-function: linear;
    background: linear-gradient(to right, #eee 8%, #ddd 18%, #eee 33%);
    background-size: 1000px 104px;
    position: relative;
    overflow: hidden;
  `,
};

export default styles;
